import type { ChessAI } from '@chessmate/chess-ai';
import type { GameAdapter } from '@chessmate/game-engine';
import type { Fen, San, Turn } from '@chessmate/protocol';
import { Chess } from 'chess.js';
import { buildFacts } from './facts.js';
import { buildExplanation, buildSpeakable } from './templates.js';

export interface CoachAdvice {
  readonly san: San;
  readonly uci: string;
  readonly scoreCp: number;
  /** Continuation after the first move (display only). */
  readonly line: string[];
  readonly explanation: string;
  readonly speakable: string;
}

export interface CoachInput {
  readonly engine: Pick<ChessAI, 'getBestMoves' | 'setPosition'>;
  readonly adapter: GameAdapter;
  readonly fen: Fen;
  readonly turn: Turn;
  readonly count?: number;
}

export async function getCoachAdvice(input: CoachInput): Promise<CoachAdvice[]> {
  const { engine, adapter, fen, turn, count = 3 } = input;
  const chess = new Chess(fen);
  await engine.setPosition(fen);
  const bestMoves = await engine.getBestMoves(count, { movetimeMs: 700 });

  const advice: CoachAdvice[] = [];
  for (const m of bestMoves) {
    const uci = m.uci;
    if (uci.length < 4) continue;
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    let san: San;
    try {
      const mv = chess.move({ from, to, promotion: 'q' });
      san = mv.san;
      chess.undo();
    } catch {
      continue;
    }
    const facts = buildFacts({ adapter, fen, from, to, turn });
    advice.push({
      san,
      uci,
      scoreCp: m.scoreCp,
      line: m.pv.slice(1),
      explanation: buildExplanation(facts),
      speakable: buildSpeakable(facts),
    });
  }
  return advice;
}
